import { z } from 'zod';
import type { Permission } from '../permissions';

// The admin iPhone app's push registration (IMM-1411.3). The operator is always the calling session's admin.
/** Expo's push token as `getExpoPushTokenAsync` returns it. */
export const ExpoPushTokenSchema = z.string().regex(/^Expo(nent)?PushToken\[[A-Za-z0-9_-]+\]$/);
export const AdminPushPlatformSchema = z.enum(['IOS']);
export type AdminPushPlatform = z.infer<typeof AdminPushPlatformSchema>;

export const RegisterAdminPushDeviceBodySchema = z.object({
  token: ExpoPushTokenSchema,
  platform: AdminPushPlatformSchema,
});
export const RegisterAdminPushDeviceResponseSchema = z.object({
  platform: AdminPushPlatformSchema,
  lastSeenAt: z.iso.datetime(),
});
export type RegisterAdminPushDeviceResponse = z.infer<typeof RegisterAdminPushDeviceResponseSchema>;

// Which push events an operator switches on (IMM-1411.4). A choice is the operator's, on every device.
export const AdminPushEventSchema = z.enum([
  'FLEET_QUESTION_PARKED',
  'FLEET_MACHINE_ENROLMENT_WAITING',
  'LANDIQ_DATA_RUN_FAILED',
  'LANDIQ_STAGE_FINISHED',
  'LANDIQ_DATA_RUN_STALE',
  'FLEET_RELEASE_DEPLOYED',
]);
export type AdminPushEvent = z.infer<typeof AdminPushEventSchema>;

/** The area an event belongs to: offered — and delivered — only to an operator holding it. */
export const ADMIN_PUSH_EVENT_AREA_PERMISSION = {
  FLEET_QUESTION_PARKED: 'backoffice:fleet:read',
  FLEET_MACHINE_ENROLMENT_WAITING: 'backoffice:fleet:read',
  LANDIQ_DATA_RUN_FAILED: 'backoffice:admin:landiq-data-run-manage',
  LANDIQ_STAGE_FINISHED: 'backoffice:admin:landiq-data-run-manage',
  LANDIQ_DATA_RUN_STALE: 'backoffice:admin:landiq-data-run-manage',
  FLEET_RELEASE_DEPLOYED: 'backoffice:fleet:read',
} as const satisfies Record<AdminPushEvent, Permission>;

export const AdminPushEventPathParamsSchema = z.object({ event: AdminPushEventSchema });
export const AdminPushEventChoiceSchema = z.object({ event: AdminPushEventSchema, enabled: z.boolean() });
export const ListAdminPushEventsResponseSchema = z.object({ events: z.array(AdminPushEventChoiceSchema) });
export const SetAdminPushEventBodySchema = z.object({ enabled: z.boolean() });
export const SetAdminPushEventResponseSchema = AdminPushEventChoiceSchema;
export type AdminPushEventChoice = z.infer<typeof AdminPushEventChoiceSchema>;
export type ListAdminPushEventsResponse = z.infer<typeof ListAdminPushEventsResponseSchema>;
export type SetAdminPushEventResponse = z.infer<typeof SetAdminPushEventResponseSchema>;
